import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useLocation } from '@tanstack/react-router'
import { AlertCircle, Eye, EyeOff } from 'lucide-react'
import { CustomerRegisterInput } from '@buddys-choice/shared'
import type { CustomerRegisterInput as CustomerRegisterInputType } from '@buddys-choice/shared'
import { apiPost } from '../lib/api'
import { useAuth, type User as AuthUser } from '../context/AuthContext'
import { CustomerAuthLayout } from '../layouts/CustomerAuthLayout'

export const CustomerRegisterPage: React.FC = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CustomerRegisterInputType>({
    resolver: zodResolver(CustomerRegisterInput),
    defaultValues: { name: '', phone: '', email: '', password: '' },
  })

  const redirectTo = (() => {
    const search = (location.search ?? {}) as Record<string, unknown>
    return typeof search.redirect === 'string' && search.redirect.startsWith('/')
      ? search.redirect
      : '/account/orders'
  })()

  const handleRegister = async (data: CustomerRegisterInputType) => {
    setError('')
    try {
      const res = await apiPost<{ token: string; user: AuthUser }>('auth/register', {
        ...data,
        email: data.email?.trim() || undefined,
      })
      login(res.token, res.user)
      navigate({ to: redirectTo, replace: true })
    } catch (err: unknown) {
      setError((err as Error).message || 'Registration failed. Please try again.')
    }
  }

  return (
    <CustomerAuthLayout
      title="Create account"
      subtitle="Required: name, mobile, password. Email is optional."
    >
      {error && (
        <div className="alert alert-error flex items-center gap-2 text-sm" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(handleRegister)} className="space-y-5">
        <div className="form-group">
          <label className="form-label" htmlFor="reg-name">Full name</label>
          <input
            id="reg-name"
            type="text"
            {...register('name')}
            autoComplete="name"
            aria-invalid={!!errors.name}
            className="form-input"
          />
          {errors.name && <span className="form-error">{errors.name.message}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="reg-phone">Mobile number</label>
          <input
            id="reg-phone"
            type="tel"
            inputMode="numeric"
            {...register('phone', {
              onChange: (e) => {
                e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
              },
            })}
            placeholder="10-digit mobile"
            autoComplete="tel"
            aria-invalid={!!errors.phone}
            className="form-input font-mono"
          />
          {errors.phone && <span className="form-error">{errors.phone.message}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="reg-email">Email <span className="font-normal text-[var(--text-faint)]">(optional)</span></label>
          <input
            id="reg-email"
            type="email"
            {...register('email')}
            autoComplete="email"
            aria-invalid={!!errors.email}
            className="form-input"
          />
          {errors.email && <span className="form-error">{errors.email.message}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="reg-password">Password</label>
          <div className="relative">
            <input
              id="reg-password"
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              autoComplete="new-password"
              aria-invalid={!!errors.password}
              className="form-input pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && <span className="form-error">{errors.password.message}</span>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-gold mt-2 w-full py-3.5 text-sm font-semibold tracking-wide disabled:opacity-60"
        >
          {isSubmitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="text-sm text-[var(--text-muted)]">
        Already have an account?{' '}
        <Link
          to="/account/login"
          search={redirectTo !== '/account/orders' ? { redirect: redirectTo } : {}}
          className="font-semibold text-[var(--gold-main)] hover:underline"
        >
          Sign in
        </Link>
      </p>
    </CustomerAuthLayout>
  )
}
